export const isMsalResponseHash = (hash: string): boolean => {
  const rawParameters = hash.replace(/^#/, "");
  if (!rawParameters.includes("=")) return false;

  const responseParameters = new URLSearchParams(rawParameters);
  return ["code", "error", "id_token", "access_token", "state", "session_state"]
    .some((parameter) => responseParameters.has(parameter));
};

export function getRedirectStartPage(currentUrl = window.location.href): string {
  const startPage = new URL(currentUrl);
  // Keep MSAL's return-to-request-page behavior, but never cache a callback response as the start URL.
  if (isMsalResponseHash(startPage.hash)) startPage.hash = "";
  return startPage.href;
}

export function shouldDeferEmbeddedAuthResponse(isEmbeddedFrame: boolean, hash: string): boolean {
  return isEmbeddedFrame && isMsalResponseHash(hash);
}

export async function handleRedirectWithCodeFallback(
  handleRedirect: () => Promise<unknown>,
  location: Pick<Location, "hash" | "origin">,
  replaceLocation: (url: string) => void,
): Promise<boolean> {
  const hasAuthCode = () => {
    const rawParameters = location.hash.replace(/^#/, "");
    return rawParameters.includes("=") && new URLSearchParams(rawParameters).has("code");
  };

  try {
    // Let MSAL consume a valid callback before considering a stale-code fallback.
    await handleRedirect();
  } catch (error) {
    if (!hasAuthCode()) throw error;
    replaceLocation(location.origin);
    return false;
  }

  if (hasAuthCode()) {
    replaceLocation(location.origin);
    return false;
  }

  return true;
}

export function createRedirectCoordinator() {
  let redirectPromise: Promise<void> | undefined;

  return (startRedirect: () => Promise<void>): Promise<void> => {
    if (!redirectPromise) {
      redirectPromise = Promise.resolve().then(startRedirect);
      void redirectPromise.catch(() => {
        redirectPromise = undefined;
      });
    }

    // Keep a successful redirect latched until the page navigates away.
    return redirectPromise;
  };
}

export const redirectOnce = createRedirectCoordinator();
